import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { formatVND } from "@/lib/format";
import { serverErrorMessage } from "@/lib/api/error-message";

export const cskhSettingsKeys = {
  catalog: ["cskh", "settings", "catalog"] as const,
  priceTable: ["cskh", "settings", "price-table"] as const,
  pings: ["cskh", "settings", "pings"] as const,
  deadline: ["cskh", "settings", "deadline"] as const,
};

export type CatalogProduct = {
  id: string;
  name: string;
  icon?: string;
  desc?: string;
  sizes?: string[];
  images?: string[];
};

export type PriceCell = number | string | null | undefined;

export type PriceTable = {
  materials: string[];
  sizes: string[];
  // prices[size][materialIndex] = VND number. Legacy settings may still carry
  // thousand-based strings such as "1.180" or empty dashes.
  prices: Record<string, PriceCell[]>;
};

export function normalizePriceCell(cell: PriceCell): number {
  if (typeof cell === "number") return Number.isFinite(cell) && cell > 0 ? Math.round(cell) : 0;
  const raw = String(cell ?? "").trim();
  if (!raw || raw === "—" || raw === "-" || /^nan$/i.test(raw)) return 0;

  const hasExplicitVnd = /(?:₫|đ|vnd|vnđ)/i.test(raw);
  const digits = raw.replace(/[^\d]/g, "");
  if (!digits) return 0;

  const n = Number(digits);
  if (!Number.isFinite(n) || n <= 0) return 0;

  // Legacy CSKH price-table cells are stored in thousand VND: "30" = 30k,
  // "1.180" = 1,180k. Already-normalized strings/currency labels stay as VND.
  return hasExplicitVnd || n >= 10_000 ? n : n * 1_000;
}

export function formatPriceTableCell(cell: PriceCell): string {
  const n = normalizePriceCell(cell);
  return n > 0 ? formatVND(n) : "—";
}

export function normalizePriceTable(table: PriceTable): PriceTable {
  const materials = Array.isArray(table.materials)
    ? table.materials.map((m) => String(m).trim()).filter(Boolean)
    : [];
  const sizes = Array.isArray(table.sizes)
    ? table.sizes.map((s) => String(s).trim()).filter(Boolean)
    : [];
  const sourcePrices = table.prices ?? {};
  const prices: Record<string, number[]> = {};
  for (const size of sizes) {
    const row = Array.isArray(sourcePrices[size]) ? sourcePrices[size] : [];
    prices[size] = materials.map((_, i) => normalizePriceCell(row[i]));
  }
  return { materials, sizes, prices };
}

const DEFAULT_PRICE_TABLE = normalizePriceTable({
  materials: ["Giấy HD", "Gỗ", "Canvas/AP", "Pha lê", "Mica HD+"],
  sizes: [
    "10x15",
    "13x18",
    "15x21",
    "20x30",
    "25x35",
    "30x40",
    "40x60",
    "50x75",
    "60x80",
    "60x90",
    "100x150",
    "120x80",
    "120x180",
  ],
  prices: {
    "10x15": ["20", "—", "—", "—", "—"],
    "13x18": ["30", "—", "—", "—", "—"],
    "15x21": ["40", "60", "100", "—", "210"],
    "20x30": ["50", "90", "150", "—", "350"],
    "25x35": ["—", "—", "—", "—", "—"],
    "30x40": ["—", "400", "750", "890", "1.180"],
    "40x60": ["—", "—", "—", "—", "—"],
    "50x75": ["—", "890", "990", "1.260", "1.400"],
    "60x80": ["—", "1.400", "2.400", "3.100", "3.800"],
    "60x90": ["—", "2.450", "2.800", "3.600", "4.480"],
    "100x150": ["—", "—", "—", "—", "—"],
    "120x80": ["—", "3.200", "4.500", "4.890", "6.900"],
    "120x180": ["—", "—", "—", "—", "—"],
  },
});

async function getSetting<T>(key: string): Promise<T | null> {
  const { data, error, response } = await api.GET("/settings/{key}", {
    params: { path: { key } },
  });
  if (response.status === 404) return null;
  if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được cài đặt"));
  return (data.value as T) ?? null;
}

async function putSetting<T>(key: string, value: T): Promise<void> {
  const { error } = await api.PUT("/settings/{key}", {
    params: { path: { key } },
    body: { value: value as unknown },
  });
  if (error) throw new Error(serverErrorMessage(error, "Không lưu được cài đặt"));
}

export function useCatalog() {
  return useQuery({
    queryKey: cskhSettingsKeys.catalog,
    queryFn: async () => (await getSetting<CatalogProduct[]>("cskh_catalog")) ?? [],
  });
}

export function useSaveCatalog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (products: CatalogProduct[]) => putSetting("cskh_catalog", products),
    onSuccess: () => void qc.invalidateQueries({ queryKey: cskhSettingsKeys.catalog }),
  });
}

export function usePriceTable() {
  return useQuery({
    queryKey: cskhSettingsKeys.priceTable,
    queryFn: async () => normalizePriceTable((await getSetting<PriceTable>("cskh_price_table")) ?? DEFAULT_PRICE_TABLE),
  });
}

export function useSavePriceTable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (table: PriceTable) => putSetting("cskh_price_table", normalizePriceTable(table)),
    onSuccess: () => void qc.invalidateQueries({ queryKey: cskhSettingsKeys.priceTable }),
  });
}

/** Lookup price for a given size + material from price table. Returns 0 if not found. */
export function lookupPrice(table: PriceTable, size: string, material: string): number {
  const matIdx = table.materials.indexOf(material);
  if (matIdx < 0) return 0;
  const row = table.prices[size];
  if (!row) return 0;
  return normalizePriceCell(row[matIdx]);
}

// ── Hạn trả ảnh sửa (CEO chốt câu 7a, 16/09/2026) ──────────────────────────
// Gương của `deadline_config.go`: mặc định 3 ngày, chỉnh trong 3–5. Máy chủ KẸP
// số lạ chứ không báo lỗi, nên màn cũng phải kẹp khi ĐỌC — không thì sổ lưu 9 mà
// màn hiện 9 trong khi máy tính hạn theo 5.

export const DEADLINE_DAYS_DEFAULT = 3;
export const DEADLINE_DAYS_MIN = 3;
export const DEADLINE_DAYS_MAX = 5;
const DEADLINE_KEY = "cskh_deadline";

/** Số ngày máy chủ thật sự áp cho một giá trị đã lưu. Không phải số nguyên
 *  (JSON hỏng, chuỗi, số lẻ) → mặc định, y như Go gỡ JSON vào `int` thất bại. */
export function clampDeadlineDays(raw: unknown): number {
  if (typeof raw !== "number" || !Number.isInteger(raw)) return DEADLINE_DAYS_DEFAULT;
  return Math.min(DEADLINE_DAYS_MAX, Math.max(DEADLINE_DAYS_MIN, raw));
}

export function useDeadlineDays() {
  return useQuery({
    queryKey: cskhSettingsKeys.deadline,
    queryFn: async () =>
      clampDeadlineDays((await getSetting<{ days?: unknown }>(DEADLINE_KEY))?.days),
  });
}

export function useSaveDeadlineDays() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (days: number) => putSetting(DEADLINE_KEY, { days }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: cskhSettingsKeys.deadline }),
  });
}
